import { ArrowRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { CommandPaletteItem } from './CommandPaletteItem';
import type { CommandProvider, CommandResult } from './types';

interface CommandPaletteGroupProps {
  provider: CommandProvider;
  results: CommandResult[];
  query: string;
  isLoading: boolean;
  isScoped: boolean;
  onSelect: (result: CommandResult) => void;
  onViewAll?: (href: string) => void;
  selectedId: string | null;
  onHover: (id: string) => void;
}

export function CommandPaletteGroup({
  provider,
  results,
  query,
  isLoading,
  isScoped,
  onSelect,
  onViewAll,
  selectedId,
  onHover,
}: CommandPaletteGroupProps) {
  if (!isScoped && !isLoading && results.length === 0) return null;

  const Icon = provider.icon;
  const viewAllHref = query && provider.viewAllHref?.(query);
  const showViewAll = viewAllHref && !isScoped && results.length >= (provider.maxResults ?? 3);

  return (
    <div className="overflow-hidden p-1">
      <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {provider.label}
        {provider.scopeKeyword && (
          <kbd className="ml-1 text-[10px] font-mono text-muted-foreground/40">
            #{provider.scopeKeyword}
          </kbd>
        )}
      </div>

      {isLoading && results.length === 0 && (
        <div className="px-2 py-1.5 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}

      {results.map((result) => (
        <CommandPaletteItem
          key={result.id}
          result={result}
          query={query}
          selected={result.id === selectedId}
          onSelect={() => onSelect(result)}
          onHover={() => onHover(result.id)}
        />
      ))}

      {showViewAll && onViewAll && (
        <button
          type="button"
          onClick={() => onViewAll(viewAllHref)}
          onMouseEnter={() => onHover(`view-all:${provider.id}`)}
          data-item-id={`view-all:${provider.id}`}
          className={`flex w-full items-center gap-2 rounded-sm px-2 py-2 text-xs text-muted-foreground cursor-default select-none ${
            selectedId === `view-all:${provider.id}` ? 'bg-accent text-accent-foreground' : ''
          }`}
        >
          <ArrowRight className="h-3.5 w-3.5" />
          View all {provider.label.toLowerCase()} results
        </button>
      )}
    </div>
  );
}
